<template>
  <el-dialog
    :title="isEdit ? '编辑消防文件' : '新增消防文件'"
    :visible.sync="dialogVisible"
    width="560px"
    :close-on-click-modal="false"
    append-to-body
    @open="handleOpen"
    @closed="handleClosed"
  >
    <el-form
      ref="form"
      :model="formData"
      :rules="formRules"
      label-width="90px"
      autocomplete="off"
    >
      <el-form-item label="文件名称" prop="fileName">
        <el-input
          v-model="formData.fileName"
          placeholder="选择文件后将自动填入文件名，也可手动修改"
          maxlength="200"
          show-word-limit
          clearable
          autocomplete="off"
        />
      </el-form-item>

      <el-form-item label="文件分类" prop="fileType">
        <dict-select
          :key="fileTypeDictSelectKey"
          v-model="formData.fileType"
          :dict-type="DICT_TYPE.FIREFILE_TYPE"
          placeholder="请选择文件分类"
          style="width: 100%"
        />
        <div class="dict-empty-hint">
          如需维护文件分类，可前往
          <el-button type="text" class="dict-empty-hint-link" @click="goDictManage(DICT_TYPE.FIREFILE_TYPE)">字段管理</el-button>
          菜单中新增或修改。
        </div>
      </el-form-item>

      <el-form-item label="上传文件" :prop="isEdit ? undefined : 'fileId'">
        <!-- 文件上传组件 -->
        <FileServeUpload
          v-model="formData.fileId"
          :show-file-list="true"
          :data="uploadData"
          :accept="'*'"
          :on-change="uploadChangeHandler"
          :on-success="uploadSuccessHandler"
          style="width: 100%"
        >
          <el-button slot="default" type="primary" plain icon="el-icon-upload2">
            {{ isEdit ? '重新上传（可选）' : '点击上传' }}
          </el-button>
          <div slot="tip" class="field-tip">支持所有文件格式，单文件大小不超过 50MB</div>
        </FileServeUpload>
        <div v-if="isEdit && originalFileName" class="field-tip current-file-tip">当前文件：{{ originalFileName }}</div>
      </el-form-item>

      <el-form-item label="备注">
        <el-input
          v-model="formData.remark"
          type="textarea"
          :rows="3"
          placeholder="备注信息"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { fireFileService } from './firefile.service';
// 工具函数
import { DICT_TYPE } from '@/features/components/dict/dictTypeKeys';
// 组件
import FileServeUpload from '@/features/components/FileServeUpload/FileServeUpload.vue';

export default {
  name: 'FirefileDialog',
  components: { FileServeUpload },
  props: {
    visible: { type: Boolean, default: false },
    /** 编辑时传入记录 uuid，新增时为 null */
    fileUuid: { type: [String, Number], default: null },
    /** 编辑时由列表行带入（后端无单独详情接口） */
    initialRecord: { type: Object, default: null }
  },
  data () {
    return {
      DICT_TYPE,
      fileTypeDictSelectKey: 0,
      formData: {
        uuid: null,
        fileName: '',
        fileType: '',
        fileId: '',
        remark: ''
      },
      originalFileName: '',
      uploadData: {
        bizType: 'fire_file',
        md5: '',
        name: '',
        fileId: ''
      },
      submitting: false,
      /** 避免模板里绑定 :on-success 方法名在部分环境下解析失败，用 data 上的函数引用 */
      uploadChangeHandler: null,
      uploadSuccessHandler: null
    };
  },
  computed: {
    dialogVisible: {
      get () { return this.visible; },
      set (val) { this.$emit('update:visible', val); }
    },
    isEdit () { return !!this.fileUuid; },
    formRules () {
      const rules = {
        fileName: [{ required: true, message: '请输入文件名称', trigger: 'blur' }],
        fileType: [{ required: true, message: '请选择文件分类', trigger: 'change' }],
        fileId: [{ required: true, message: '请上传文件', trigger: 'change' }]
      };
      if (this.isEdit) {
        delete rules.fileId;
      }
      return rules;
    }
  },
  methods: {
    goDictManage (dictType) {
      this.dialogVisible = false;
      const query = dictType ? { dictType } : {};
      this.$nextTick(() => {
        this.openNavTab({ name: 'dictList', query });
      });
    },
    handleOpen () {
      this.fileTypeDictSelectKey += 1;
      this.resetForm();
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
      if (this.isEdit && this.initialRecord) {
        this.applyRecord(this.initialRecord);
      }
    },
    handleClosed () {
      this.resetForm();
    },
    resetForm () {
      this.formData.uuid = null;
      this.formData.fileName = '';
      this.formData.fileType = '';
      this.formData.fileId = '';
      this.formData.remark = '';
      this.originalFileName = '';
    },
    applyRecord (row) {
      if (this._isDestroyed || !this.visible) return;
      if (String(row.uuid) !== String(this.fileUuid)) return;
      this.formData.uuid = row.uuid;
      this.formData.fileName = row.fileName || '';
      this.formData.fileType = row.fileType || '';
      this.formData.fileId = '';
      this.formData.remark = row.remark || '';
      this.originalFileName = row.fileName || '';
    },
    clipFileNameForUpload (name) {
      if (!name || typeof name !== 'string') return '';
      return name.length > 200 ? name.slice(0, 200) : name;
    },
    handleSubmit () {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        this.submitting = true;
        const payload = { ...this.formData };
        if (this.isEdit && !payload.fileId) {
          delete payload.fileId;
        }
        const request = this.isEdit
          ? fireFileService.update(payload)
          : fireFileService.save(payload);

        request.then(() => {
          this.$message.success(this.isEdit ? '编辑成功' : '新增成功');
          this.dialogVisible = false;
          this.$emit('success');
        }).finally(() => {
          this.submitting = false;
        });
      });
    }
  },
  created () {
    this.uploadChangeHandler = (file) => {
      if (file && file.status === 'ready' && file.name) {
        this.formData.fileName = this.clipFileNameForUpload(file.name);
      }
    };
    this.uploadSuccessHandler = (response, file) => {
      const name = (response && response.name) || (file && file.name) || '';
      if (name) {
        this.formData.fileName = this.clipFileNameForUpload(name);
      }
    };
  }
};
</script>

<style scoped lang="scss">
.field-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.current-file-tip {
  color: #606266;
}

::v-deep .el-form-item {
  margin-bottom: 18px;
}

::v-deep .el-form-item__error {
  position: relative;
  top: 0;
  padding-top: 2px;
}

.dict-empty-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.dict-empty-hint-link {
  padding: 0;
  vertical-align: baseline;
  font-size: 12px;
}
</style>
